import React, { useEffect, useState } from "react";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
    Bell, AlertTriangle, CalendarClock, Copy, ShoppingBag, TrendingDown,
    Check, X, Smartphone,
} from "lucide-react";
import {
    isPushSupported, getPushPermission, enablePushNotifications, sendTestPush,
} from "../lib/push";

// Map an alert kind → icon
const KIND_ICON = {
    bill_due_tomorrow: CalendarClock,
    missed_bill: AlertTriangle,
    duplicate_charge: Copy,
    new_merchant_large: ShoppingBag,
    forecast_negative: TrendingDown,
};

// Severity → colour token used on the icon tile
const SEVERITY_COLOR = {
    warning: "text-amber-500 bg-amber-500/10",
    info: "text-sky-500 bg-sky-500/10",
    success: "text-emerald-500 bg-emerald-500/10",
};

const Notifications = () => {
    const { language } = useApp();
    const isEs = language === "es";
    const [data, setData] = useState({ alerts: [], unread_count: 0 });
    const [pushState, setPushState] = useState({ supported: false, permission: "default", subscribed: false });

    const load = async () => {
        try {
            const r = await api.get("/alerts");
            setData(r.data);
        } catch { setData({ alerts: [], unread_count: 0 }); }
    };

    useEffect(() => {
        load();
        setPushState({
            supported: isPushSupported(),
            permission: getPushPermission(),
            subscribed: false,
        });
    }, []);

    const enablePush = async () => {
        const res = await enablePushNotifications();
        if (res.ok) {
            setPushState((s) => ({ ...s, permission: "granted", subscribed: true }));
            toast.success(isEs ? "Notificaciones activadas en este dispositivo" : "Notifications enabled on this device");
        } else if (res.reason === "denied") {
            toast.error(isEs
                ? "Bloqueadas por el navegador. Actívalas en los ajustes del sitio."
                : "Blocked by your browser. Enable in the site settings.");
        } else if (res.reason === "unsupported") {
            toast.error(isEs
                ? "Este navegador no soporta notificaciones push."
                : "This browser doesn't support push notifications.");
        } else {
            toast.error(isEs ? "No se pudo activar. Intenta de nuevo." : "Couldn't enable — try again.");
        }
    };

    const testPush = async () => {
        const r = await sendTestPush();
        if (r?.sent > 0) toast.success(isEs ? "Enviada a este dispositivo" : "Sent to this device");
        else toast.error(isEs ? "Aún no hay dispositivos suscritos" : "No devices subscribed yet");
    };

    const markRead = async (dedupe_key) => {
        await api.post(`/alerts/${encodeURIComponent(dedupe_key)}/read`);
        load();
    };
    const dismiss = async (dedupe_key) => {
        await api.delete(`/alerts/${encodeURIComponent(dedupe_key)}`);
        load();
    };
    const markAllRead = async () => {
        await api.post("/alerts/mark-all-read");
        load();
    };

    const canOfferPush = pushState.supported && pushState.permission !== "granted";
    const pushEnabled = pushState.supported && pushState.permission === "granted";

    return (
        <AppLayout title={t(language, "nav_notifications")}>
            {/* Phone banner enrollment card */}
            <div className="mb-6 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 flex items-start gap-4"
                 data-testid="push-enrollment-card">
                <div className="w-11 h-11 rounded-xl bg-emerald-500/15 text-emerald-500 grid place-items-center shrink-0">
                    <Smartphone className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-base md:text-lg leading-tight">
                        {isEs ? "Recibe alertas en tu teléfono" : "Get alerts on your phone"}
                    </div>
                    <div className="text-sm text-[hsl(var(--muted-foreground))] mt-0.5 leading-snug">
                        {isEs
                            ? "Facturas próximas, cargos duplicados y avisos de flujo negativo llegarán como notificación. En iPhone, primero agrega la app a tu pantalla de inicio."
                            : "Upcoming bills, duplicate charges and cash-flow warnings arrive as a banner. On iPhone, first add the app to your Home Screen."}
                    </div>
                    <div className="mt-3 flex gap-2 flex-wrap">
                        {canOfferPush && (
                            <Button data-testid="enable-push-btn" size="sm" onClick={enablePush}>
                                {pushState.permission === "denied"
                                    ? (isEs ? "Cómo desbloquear" : "How to unblock")
                                    : (isEs ? "Activar notificaciones" : "Enable notifications")}
                            </Button>
                        )}
                        {pushEnabled && (
                            <>
                                <Button data-testid="push-enabled-badge" size="sm" variant="secondary" disabled>
                                    <Check className="w-4 h-4 mr-1" /> {isEs ? "Activado" : "Enabled"}
                                </Button>
                                <Button data-testid="test-push-btn" size="sm" variant="outline" onClick={testPush}>
                                    {isEs ? "Enviar prueba" : "Send test"}
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Alerts list */}
            <Card className="rounded-2xl bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                <CardContent className="p-0">
                    <div className="flex items-center justify-between px-6 py-4 border-b border-[hsl(var(--border))]">
                        <div className="font-display font-bold text-base md:text-lg">
                            {isEs ? "Alertas" : "Alerts"}
                            {data.unread_count > 0 && (
                                <span className="ml-2 rounded-full bg-red-500 text-white text-[10px] font-black px-2 py-0.5 align-middle"
                                      data-testid="unread-count-badge">
                                    {data.unread_count}
                                </span>
                            )}
                        </div>
                        {data.unread_count > 0 && (
                            <button
                                data-testid="mark-all-read"
                                onClick={markAllRead}
                                className="text-xs font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                            >
                                {isEs ? "Marcar todo como leído" : "Mark all read"}
                            </button>
                        )}
                    </div>
                    {data.alerts.length === 0 ? (
                        <div className="p-10 text-center text-[hsl(var(--muted-foreground))]" data-testid="alerts-empty">
                            <Bell className="w-6 h-6 mx-auto mb-2 opacity-60" />
                            {isEs ? "Sin alertas por ahora — todo bajo control." : "No alerts — you're in the clear."}
                        </div>
                    ) : (
                        <div className="divide-y divide-[hsl(var(--border))]">
                            {data.alerts.map((n) => {
                                const Icon = KIND_ICON[n.kind] || Bell;
                                const tile = SEVERITY_COLOR[n.severity] || "text-slate-500 bg-slate-500/10";
                                return (
                                    <div key={n.dedupe_key}
                                         data-testid={`alert-row-${n.kind}`}
                                         className={`flex items-start gap-4 px-6 py-4 ${n.read ? "opacity-70" : ""}`}>
                                        <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${tile}`}>
                                            <Icon className="w-4 h-4" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-semibold text-[hsl(var(--foreground))]">
                                                {isEs ? n.title_es : n.title_en}
                                            </div>
                                            <div className="text-sm text-[hsl(var(--muted-foreground))] mt-0.5">
                                                {isEs ? n.message_es : n.message_en}
                                            </div>
                                        </div>
                                        <div className="flex flex-col items-end gap-1 shrink-0">
                                            {!n.read && (
                                                <button
                                                    data-testid={`mark-read-${n.kind}`}
                                                    onClick={() => markRead(n.dedupe_key)}
                                                    className="text-[hsl(var(--muted-foreground))] hover:text-emerald-500 p-1"
                                                    title={isEs ? "Marcar leído" : "Mark read"}
                                                >
                                                    <Check className="w-4 h-4" />
                                                </button>
                                            )}
                                            <button
                                                data-testid={`dismiss-${n.kind}`}
                                                onClick={() => dismiss(n.dedupe_key)}
                                                className="text-[hsl(var(--muted-foreground))] hover:text-red-500 p-1"
                                                title={isEs ? "Descartar" : "Dismiss"}
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>
        </AppLayout>
    );
};

export default Notifications;
